import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useGetUserProfileQuery } from '../../store/api/userApi';
import { IS_TENANT_MODE, TENANT_LOGIN_HOME } from '../../config/tenantMode';
import { clearAuthData, isAuthenticated, storeAuthData } from '../../utils/tokenManager';

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const location = useLocation();
  const auth = isAuthenticated();

  const { data, isLoading, isError } = useGetUserProfileQuery(undefined, {
    skip: !auth,
  });

  const user = data?.data?.user;

  // Keep localStorage.user in sync (isAdmin, name) so App maintenance bypass stays correct
  useEffect(() => {
    if (!user) return;
    try {
      const prev = JSON.parse(localStorage.getItem('user') || '{}') as Record<string, unknown>;
      storeAuthData('', { ...prev, ...user });
    } catch {
      storeAuthData('', user as unknown as Record<string, unknown>);
    }
  }, [user]);

  if (!auth) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-medium text-xl font-body animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  if (isError || !user) {
    clearAuthData();
    return <Navigate to={IS_TENANT_MODE ? TENANT_LOGIN_HOME : '/login'} replace />;
  }

  if (!user.isAdmin) {
    // Tenant stores are merchant-admin only — drop bad sessions instead of looping
    if (IS_TENANT_MODE) {
      clearAuthData();
      return <Navigate to={TENANT_LOGIN_HOME} replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
